/* eslint-disable prettier/prettier */
import {
  createContext,
  ReactNode,
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";
import { useSubscription, useQuery } from "@apollo/client";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import INBOX_DETAIL_ADDED from "src/graphql/subscriptions/chat/inboxdetailadded";
import INBOX_DETAIL_CHANGED from "src/graphql/subscriptions/chat/inboxdetailchanged";
import MARK_ALL_MESSAGE_READ from "src/graphql/subscriptions/chat/markallmessageread";
import INBOX_POST_ADDED from "src/graphql/subscriptions/comments/inboxpostadded";
import MARK_POST_READ from "src/graphql/subscriptions/comments/markpostread";
import GetUnreadChatCountsQuery from "src/graphql/query/chat/getUnreadChatCounts";
import { useFeature } from "src/context/global";
import { CHATBOT_CONVERSATION_UPDATED } from "src/graphql/subscriptions/chatbot/chatbotMonitoring";
import { IChatNote } from "src/components/pages/chat/ChatDetails/NotesBox/Types";

interface IChatNotesLoader {
  getChatNotesLoading: boolean;
  updateChatNotesLoading: boolean;
}

export interface IFacebookPost {
  id: string;
  post_id: string;
  page_id: string;
  customerId: string | null;
  agentId: string | null;
  message: string | null;
  attachments: string | null;
  created_time: number;
  updated_time: string | null;
  author_facebook_id: string | null;
  author_name: string | null;
  author_profile_picture: string | null;
  customer_name: string | null;
  customer_picture: string | null;
  total_comments_count: number;
  last_seen_at: boolean | null;
  closed_at: string | null;
  pageName: string | null;
  accesstoken: string | null;
}

export interface IFacebookComment {
  id: number;
  comment_id: string;
  parent_id: string | null;
  author_facebook_id: string;
  author_name: string;
  author_profile_picture: string | null;
  message: string | null;
  photo: string | null;
  video: string | null;
  created_time: string;
  is_hidden_from_page: boolean;
  is_sent_by_page: number;
  inbox_id: number;
  is_reply_to_comment: boolean;
  hasMoreReplies?: boolean;
  facebook_post_id: number;
  page_id: string;
  agent_id: string;
  // Nested replies (may be undefined or empty)
  replies?: IFacebookComment[];
}

export interface IChatItem {
  id: string;
  messageId: string;
  source: string;
  messagetext: string;
  messagetimestamp: string;
  customerName: string;
  customerPicture: string;
  chatorder: null | string;
  labels: string;
  marknottoaddinchatcircle: string;
  read: number;
  salesDone: string;
  customerId: string;
  pageId: string;
  pageName: string;
  accesstoken: string;
  agentId: string;
}

export interface ItoBeRepliedMessage {
  id: string;
  messageId: string;
  messagetext: string;
  deliverytimestamp: string;
  receiptreadtimestamp: string;
  messagetimestamp: string;
  messagetype: string;
  file: string;
  mediatype: string;
  replyMessage: IChatItem;
}

export interface ICommentItem {
  id: string;
  agentId: string;
  post_id: string;
  author_facebook_id: string;
  author_name: string;
  author_profile_picture: string | null;
  customerId: string;
  customer_name: string;
  customer_picture: string;
  page_id: string;
  pageName: string;
  message: string;
  attachments: string | null;
  total_comments_count: number;
  created_time: string;
  updated_time: string;
  is_replied: string | null;
  last_seen_at: string | null;
  accesstoken: string;
}

interface ISavedData {
  id: string;
  short: string;
  long: string;
  count?: number | null;
}

interface IReplyMode {
  mode: "add" | "edit" | "delete" | "count";
  item: ISavedData | null;
  formMode?: "add" | "edit" | null;
}

interface IUpdatedPostClosedTime {
  facebookPostId: number;
  agentId: number;
  closed_at: string;
  updated_time: string;
}

// createContext
interface IContextData {
  showUserDetail: boolean;
  setShowUserDetail: React.Dispatch<React.SetStateAction<boolean>>;
  //
  filter: string;
  setFilter: React.Dispatch<React.SetStateAction<string>>;
  //
  userData: { name: string; userId?: number; id?: number } | null;
  setUserData: React.Dispatch<
    React.SetStateAction<{ name: string; userId?: number; id?: number } | null>
  >;
  // chatnotes
  chatNoteData: IChatNote[];
  setChatNoteData: React.Dispatch<React.SetStateAction<IChatNote[]>>;
  //
  renderingChatNoteData: IChatNote[];
  setRenderingChatNoteData: React.Dispatch<React.SetStateAction<IChatNote[]>>;
  //
  update: boolean;
  setUpdate: React.Dispatch<React.SetStateAction<boolean>>;
  // searchbar
  showSearchBar: boolean;
  setShowSearchBar: React.Dispatch<React.SetStateAction<boolean>>;
  // tabMonitor
  tabMonitor: number;
  setTabMonitor: React.Dispatch<React.SetStateAction<number>>;
  // commentTabMonitor
  commentTabMonitor: number;
  setCommentTabMonitor: React.Dispatch<React.SetStateAction<number>>;
  // list search value
  listSearchValue: string;
  setListSearchValue: React.Dispatch<React.SetStateAction<string>>;
  // search by searchInboxId
  listSearchInboxId: string;
  setListSearchInboxId: React.Dispatch<React.SetStateAction<string>>;
  // searchList Loader
  searchListLoader: boolean;
  setSearchListLoader: React.Dispatch<React.SetStateAction<boolean>>;
  // chatList Loader
  chatListLoader: boolean;
  setChatListLoader: React.Dispatch<React.SetStateAction<boolean>>;
  //
  replyMode: IReplyMode | null;
  setReplyMode: React.Dispatch<React.SetStateAction<IReplyMode | null>>;
  //
  responseList: ISavedData[] | undefined;
  setResponseList: React.Dispatch<
    React.SetStateAction<ISavedData[] | undefined>
  >;
  //
  fieldText: string;
  setFieldText: React.Dispatch<React.SetStateAction<string>>;
  //
  updatedPostClosedTime: IUpdatedPostClosedTime | undefined;
  setUpdatedPostClosedTime: React.Dispatch<
    React.SetStateAction<IUpdatedPostClosedTime | undefined>
  >;
  //
  fieldMode: string;
  setFieldMode: React.Dispatch<React.SetStateAction<string>>;
  //
  uploadedFile: File | null;
  setUploadedFile: React.Dispatch<React.SetStateAction<File | null>>;
  //
  showCustomerMessages: IChatItem | null | IFacebookPost;
  setShowCustomerMessages: React.Dispatch<
    React.SetStateAction<IChatItem | null | IFacebookPost>
  >;
  //
  chatNoteEditMode: IChatNote | null;
  setChatNoteEditMode: React.Dispatch<React.SetStateAction<IChatNote | null>>;
  //
  chatNoteLoading: IChatNotesLoader;
  setChatNoteLoading: React.Dispatch<React.SetStateAction<IChatNotesLoader>>;
  //
  emoji: string;
  setEmoji: React.Dispatch<React.SetStateAction<string>>;

  topTabValue: number;
  setTopTabValue: React.Dispatch<React.SetStateAction<number>>;

  agentId: string;
  setAgentId: React.Dispatch<React.SetStateAction<string>>;
  // phoneNumber: string;
  // setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  removeTransferChat: string | undefined | null;
  setRemoveTransferChat: React.Dispatch<
    React.SetStateAction<string | undefined | null>
  >;
  fbCommentsUnreadCount: number;
  setFbCommentsUnreadCount: React.Dispatch<React.SetStateAction<number>>;

  fbUnreadCount: number;
  setFbUnreadCount: React.Dispatch<React.SetStateAction<number>>;

  IgUnreadCount: number;
  setIgUnreadCount: React.Dispatch<React.SetStateAction<number>>;

  WaUnreadCount: number;
  setWaUnreadCount: React.Dispatch<React.SetStateAction<number>>;

  chatbotUnreadCount: number;
  setChatbotUnreadCount: React.Dispatch<React.SetStateAction<number>>;

  postCommentsCount: number;
  setPostCommentsCount: React.Dispatch<React.SetStateAction<number>>;

  isSmsModalOpen: boolean;
  setIsSmsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // ms-since-epoch of the customer's most recent incoming message for
  // the currently-open chat. Written by ChatBox whenever the message
  // list updates; read by Header (via useReplyWindow) to render the
  // reply-window badge. Null when no incoming message exists or when
  // no chat is open.
  lastIncomingMessageTimestamp: number | null;
  setLastIncomingMessageTimestamp: React.Dispatch<
    React.SetStateAction<number | null>
  >;
}

const context = createContext<IContextData>({
  showUserDetail: false,
  setShowUserDetail: () => {},
  filter: "",
  setFilter: () => {},
  userData: null,
  setUserData: () => {},
  // chatnotes
  chatNoteData: [],
  setChatNoteData: () => {},
  //
  renderingChatNoteData: [],
  setRenderingChatNoteData: () => {},
  //
  update: false,
  setUpdate: () => {},
  // searchbar
  showSearchBar: false,
  setShowSearchBar: () => {},
  // tabmonitor
  tabMonitor: 0,
  setTabMonitor: () => {},
  // tabMonitor
  commentTabMonitor: 0,
  setCommentTabMonitor: () => {},
  // list search value
  listSearchValue: "",
  setListSearchValue: () => {},
  // search by searchInboxId
  listSearchInboxId: "",
  setListSearchInboxId: () => {},
  // searchList Loader
  searchListLoader: false,
  setSearchListLoader: () => {},
  // chatList Loader
  chatListLoader: false,
  setChatListLoader: () => {},
  //
  replyMode: null,
  setReplyMode: () => {},
  //
  responseList: undefined,
  setResponseList: () => {},
  //
  fieldText: "",
  setFieldText: () => {},
  //
  updatedPostClosedTime: undefined,
  setUpdatedPostClosedTime: () => {},
  //
  fieldMode: "",
  setFieldMode: () => {},
  //
  uploadedFile: null,
  setUploadedFile: () => {},
  //
  showCustomerMessages: null,
  setShowCustomerMessages: () => {},
  //
  chatNoteEditMode: null,
  setChatNoteEditMode: () => {},
  //
  chatNoteLoading: {
    getChatNotesLoading: false,
    updateChatNotesLoading: false,
  },
  setChatNoteLoading: () => {},
  //
  emoji: "",
  setEmoji: () => {},

  topTabValue: 0,
  setTopTabValue: () => {},

  fbCommentsUnreadCount: 0,
  setFbCommentsUnreadCount: () => {},

  fbUnreadCount: 0,
  setFbUnreadCount: () => {},

  IgUnreadCount: 0,
  setIgUnreadCount: () => {},

  WaUnreadCount: 0,
  setWaUnreadCount: () => {},

  chatbotUnreadCount: 0,
  setChatbotUnreadCount: () => {},

  postCommentsCount: 0,
  setPostCommentsCount: () => {},

  agentId: "",
  setAgentId: () => {},

  // phoneNumber: "",
  // setPhoneNumber: () => { },

  removeTransferChat: null,
  setRemoveTransferChat: () => {},

  isSmsModalOpen: false,
  setIsSmsModalOpen: () => {},

  lastIncomingMessageTimestamp: null,
  setLastIncomingMessageTimestamp: () => {},
});

// contextProvider
interface IProvider {
  children: ReactNode;
}

const ContextProvider = ({ children }: IProvider) => {
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [filter, setFilter] = useState("");

  const [userData, setUserData] = useState<{
    name: string;
    userId?: number;
    id?: number;
  } | null>(null);

  // for chatnotes
  const [chatNoteData, setChatNoteData] = useState<IChatNote[]>([]);
  const [renderingChatNoteData, setRenderingChatNoteData] = useState<
    IChatNote[]
  >([]);

  const [update, setUpdate] = useState<boolean>(false);

  const [showSearchBar, setShowSearchBar] = useState<boolean>(false);
  //
  const [tabMonitor, setTabMonitor] = useState<number>(0);
  const [listSearchValue, setListSearchValue] = useState<string>("");
  const [listSearchInboxId, setListSearchInboxId] = useState<string>("");
  // commentTabMonitor
  const [commentTabMonitor, setCommentTabMonitor] = useState<number>(0);

  // searchloader
  const [chatListLoader, setChatListLoader] = useState<boolean>(false);
  const [searchListLoader, setSearchListLoader] = useState<boolean>(false);
  //
  const [replyMode, setReplyMode] = useState<IReplyMode | null>(null);
  const [responseList, setResponseList] = useState<ISavedData[] | undefined>(
    [],
  );
  const [fieldText, setFieldText] = useState<string>("");
  const [fieldMode, setFieldMode] = useState<string>("");

  const [updatedPostClosedTime, setUpdatedPostClosedTime] = useState<
    IUpdatedPostClosedTime | undefined
  >(undefined);
  //
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  //
  const [showCustomerMessages, setShowCustomerMessages] = useState<
    IChatItem | null | IFacebookPost
  >(null);
  //
  const [chatNoteEditMode, setChatNoteEditMode] = useState<IChatNote | null>(
    null,
  );
  //
  const [chatNoteLoading, setChatNoteLoading] = useState<IChatNotesLoader>({
    getChatNotesLoading: false,
    updateChatNotesLoading: false,
  });
  //
  const [emoji, setEmoji] = useState<string>("");

  // tab value for top most header all messags , fb ,wa
  const [topTabValue, setTopTabValue] = useState(0);

  const [fbCommentsUnreadCount, setFbCommentsUnreadCount] = useState(0);
  const [fbUnreadCount, setFbUnreadCount] = useState(0);
  const [IgUnreadCount, setIgUnreadCount] = useState(0);
  const [WaUnreadCount, setWaUnreadCount] = useState(0);
  const [chatbotUnreadCount, setChatbotUnreadCount] = useState(0);

  const [, setFbUnreadIds] = useState<Set<string>>(new Set());
  const [, setIgUnreadIds] = useState<Set<string>>(new Set());
  const [, setWaUnreadIds] = useState<Set<string>>(new Set());
  const [, setCommentUnreadIds] = useState<Set<string>>(new Set());
  const [, setChatbotUnreadIds] = useState<Set<string>>(new Set());

  const [postCommentsCount, setPostCommentsCount] = useState(0);
  const [agentId, setAgentId] = useState("");
  // const [phoneNumber, setPhoneNumber] = useState("");
  const [removeTransferChat, setRemoveTransferChat] = useState<
    string | undefined | null
  >(null);

  const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);

  const [lastIncomingMessageTimestamp, setLastIncomingMessageTimestamp] =
    useState<number | null>(null);
  const { canUse } = useFeature();
  const canSeeChatbot = canUse("see_chatbot_conversations");

  // -------------------------

  const { agentId: currentAgentId, panelType } = useActiveUserDetail();

  // --- Initial Fetch ---
  const { data: unreadCountsData } = useQuery(GetUnreadChatCountsQuery, {
    variables: { agentId: currentAgentId },
    skip: !currentAgentId,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (unreadCountsData?.getUnreadChatCounts) {
      const {
        facebook,
        instagram,
        whatsapp,
        comments,
        chatbot,
        facebookIds,
        instagramIds,
        whatsappIds,
        commentsIds,
        chatbotConversationIds,
      } = unreadCountsData.getUnreadChatCounts;

      setFbUnreadIds(new Set(facebookIds || []));
      setIgUnreadIds(new Set(instagramIds || []));
      setWaUnreadIds(new Set(whatsappIds || []));
      setCommentUnreadIds(new Set(commentsIds || []));

      // Enforcement: Reset if chatbot is not allowed
      if (!canSeeChatbot) {
        setChatbotUnreadIds(new Set());
        setChatbotUnreadCount(0);
      } else {
        setChatbotUnreadIds(new Set(chatbotConversationIds || []));
        setChatbotUnreadCount(chatbot || 0);
      }

      setFbUnreadCount(facebook);
      setIgUnreadCount(instagram);
      setWaUnreadCount(whatsapp);
      setFbCommentsUnreadCount(comments);
    }
  }, [unreadCountsData, canSeeChatbot]);

  // --- Subscriptions ---
  const { data: inboxPostAddedSubscriptionResult } =
    useSubscription(INBOX_POST_ADDED);

  useEffect(() => {
    const newPost = inboxPostAddedSubscriptionResult?.inboxpostadded;
    if (!newPost || newPost.agentId !== currentAgentId) return;

    if (newPost.last_seen_at === null) {
      setCommentUnreadIds((prev) => {
        const next = new Set(prev);
        if (!next.has(newPost.id)) {
          next.add(newPost.id);
          setFbCommentsUnreadCount(next.size);
        }
        return next;
      });
    }
  }, [inboxPostAddedSubscriptionResult, currentAgentId]);

  // Helper to process incoming social message updates
  const handleInboxDetailUpdate = (newItem: any) => {
    if (!newItem || newItem.agentId !== currentAgentId) return;

    const inboxId = String(newItem.inboxId || newItem.id);

    if (!newItem.read) {
      // Logic for unread message
      const updateSet = (prevSet: Set<string>, setter: (n: number) => void) => {
        if (!prevSet.has(inboxId)) {
          const next = new Set(prevSet);
          next.add(inboxId);
          setter(next.size);
          return next;
        }
        return prevSet;
      };

      switch (newItem.source) {
        case "facebook":
          setFbUnreadIds((prev) => updateSet(prev, setFbUnreadCount));
          break;
        case "instagram":
          setIgUnreadIds((prev) => updateSet(prev, setIgUnreadCount));
          break;
        case "whatsapp":
          setWaUnreadIds((prev) => updateSet(prev, setWaUnreadCount));
          break;
        default:
          break;
      }
    } else {
      // If it became read (e.g. by another agent or system event)
      const removeId = (prevSet: Set<string>, setter: (n: number) => void) => {
        if (prevSet.has(inboxId)) {
          const next = new Set(prevSet);
          next.delete(inboxId);
          setter(next.size);
          return next;
        }
        return prevSet;
      };

      switch (newItem.source) {
        case "facebook":
          setFbUnreadIds((prev) => removeId(prev, setFbUnreadCount));
          break;
        case "instagram":
          setIgUnreadIds((prev) => removeId(prev, setIgUnreadCount));
          break;
        case "whatsapp":
          setWaUnreadIds((prev) => removeId(prev, setWaUnreadCount));
          break;
        default:
          break;
      }
    }
  };

  // When a new chat arrives or moves to top
  const { data: InboxDetailAddedSubscriptionResult } =
    useSubscription(INBOX_DETAIL_ADDED);
  const { data: InboxDetailChangedSubscriptionResult } =
    useSubscription(INBOX_DETAIL_CHANGED);

  useEffect(() => {
    handleInboxDetailUpdate(
      InboxDetailAddedSubscriptionResult?.inboxdetailadded,
    );
  }, [InboxDetailAddedSubscriptionResult, currentAgentId]);

  useEffect(() => {
    handleInboxDetailUpdate(
      InboxDetailChangedSubscriptionResult?.inboxdetailchanged,
    );
  }, [InboxDetailChangedSubscriptionResult, currentAgentId]);

  // When all messages in a chat are marked as read
  const { data: markAllMessageChatReadSubscriptionResult } = useSubscription(
    MARK_ALL_MESSAGE_READ,
  );
  useEffect(() => {
    const data =
      markAllMessageChatReadSubscriptionResult?.markallmessagechatread;
    if (data && data.agentId === currentAgentId) {
      const inboxId = String(data.inboxId);
      const removeId = (prevSet: Set<string>, setter: (n: number) => void) => {
        if (prevSet.has(inboxId)) {
          const next = new Set(prevSet);
          next.delete(inboxId);
          setter(next.size);
          return next;
        }
        return prevSet;
      };

      switch (data.source) {
        case "facebook":
          setFbUnreadIds((prev) => removeId(prev, setFbUnreadCount));
          break;
        case "instagram":
          setIgUnreadIds((prev) => removeId(prev, setIgUnreadCount));
          break;
        case "whatsapp":
          setWaUnreadIds((prev) => removeId(prev, setWaUnreadCount));
          break;
        default:
          break;
      }
    }
  }, [markAllMessageChatReadSubscriptionResult, currentAgentId]);

  // When a post (comments) is marked as read
  const { data: markPostReadSubscriptionResult } =
    useSubscription(MARK_POST_READ);
  useEffect(() => {
    const data = markPostReadSubscriptionResult?.markpostread;
    if (data && data.agentId === currentAgentId) {
      const postId = String(data.facebookPostId);
      setCommentUnreadIds((prev) => {
        if (prev.has(postId)) {
          const next = new Set(prev);
          next.delete(postId);
          setFbCommentsUnreadCount(next.size);
          return next;
        }
        return prev;
      });
    }
  }, [markPostReadSubscriptionResult, currentAgentId]);

  // --- Chatbot subscription for real-time unread count updates ---
  const { mainSuperAdminId } = useActiveUserDetail();

  const { data: chatbotConvSubData } = useSubscription(
    CHATBOT_CONVERSATION_UPDATED,
    {
      variables: { mainSuperAdminId },
      skip: !mainSuperAdminId || !canSeeChatbot,
    },
  );

  useEffect(() => {
    if (!canSeeChatbot) {
      setChatbotUnreadCount(0);
      setChatbotUnreadIds(new Set());
      return;
    }

    const updated = chatbotConvSubData?.chatbotConversationUpdated;
    if (!updated) return;

    // --- Identity & Role Check ---
    const belongsToMe =
      Number(updated.handedOffToAgentId) === Number(currentAgentId);
    const isUnassigned =
      !updated.handedOffToAgentId || Number(updated.handedOffToAgentId) === 0;
    const isAdminOrManager = panelType !== "AGENT";

    // 1. Identity & Role Check: Only process if it belongs to me OR it's unassigned and I'm an Admin/Manager
    if (!belongsToMe && !(isUnassigned && isAdminOrManager)) {
      return;
    }

    // 2. Manager Visibility Rule: Ignore 'active' (AI-only) chats
    if (panelType === "MANAGER" && updated.status === "active") {
      return;
    }

    const convId = String(updated.conversationId);
    if (updated.read === 0) {
      // New unread message
      setChatbotUnreadIds((prev) => {
        if (prev.has(convId)) return prev;
        const next = new Set(prev);
        next.add(convId);
        setChatbotUnreadCount(next.size);
        return next;
      });
    } else {
      // Marked as read
      setChatbotUnreadIds((prev) => {
        if (!prev.has(convId)) return prev;
        const next = new Set(prev);
        next.delete(convId);
        setChatbotUnreadCount(next.size);
        return next;
      });
    }
  }, [chatbotConvSubData]);

  // -------------------------

  const contextValue = useMemo(
    () => ({
      showUserDetail,
      setShowUserDetail,
      //
      filter,
      setFilter,
      //
      userData,
      setUserData,
      //
      chatNoteData,
      setChatNoteData,
      //
      renderingChatNoteData,
      setRenderingChatNoteData,
      //
      update,
      setUpdate,
      //
      showSearchBar,
      setShowSearchBar,
      //
      tabMonitor,
      setTabMonitor,
      //
      commentTabMonitor,
      setCommentTabMonitor,
      // list search value
      listSearchValue,
      setListSearchValue,
      // search by searchInboxId
      listSearchInboxId,
      setListSearchInboxId,
      // searchListLoader
      searchListLoader,
      setSearchListLoader,
      // chatListLoader
      chatListLoader,
      setChatListLoader,
      //
      replyMode,
      setReplyMode,
      //
      responseList,
      setResponseList,
      //
      fieldText,
      setFieldText,
      //
      updatedPostClosedTime,
      setUpdatedPostClosedTime,
      //
      fieldMode,
      setFieldMode,
      //
      uploadedFile,
      setUploadedFile,
      //
      showCustomerMessages,
      setShowCustomerMessages,
      //
      chatNoteEditMode,
      setChatNoteEditMode,
      //
      chatNoteLoading,
      setChatNoteLoading,
      //
      emoji,
      setEmoji,
      topTabValue,
      setTopTabValue,
      agentId,
      setAgentId,
      // phoneNumber,
      // setPhoneNumber,
      removeTransferChat,
      setRemoveTransferChat,
      //
      fbCommentsUnreadCount,
      setFbCommentsUnreadCount,
      //
      fbUnreadCount,
      setFbUnreadCount,
      //
      IgUnreadCount,
      setIgUnreadCount,
      //
      WaUnreadCount,
      setWaUnreadCount,
      //
      chatbotUnreadCount,
      setChatbotUnreadCount,
      //
      postCommentsCount,
      setPostCommentsCount,
      isSmsModalOpen,
      setIsSmsModalOpen,
      lastIncomingMessageTimestamp,
      setLastIncomingMessageTimestamp,
    }),
    [
      showUserDetail,
      filter,
      userData,
      chatNoteData,
      renderingChatNoteData,
      update,
      showSearchBar,
      tabMonitor,
      commentTabMonitor,
      listSearchValue,
      listSearchInboxId,
      searchListLoader,
      chatListLoader,
      replyMode,
      responseList,
      fieldText,
      updatedPostClosedTime,
      fieldMode,
      uploadedFile,
      //
      showCustomerMessages,
      chatNoteEditMode,
      //
      chatNoteLoading,
      emoji,
      topTabValue,
      agentId,
      // phoneNumber,
      removeTransferChat,
      //
      fbCommentsUnreadCount,
      fbUnreadCount,
      IgUnreadCount,
      WaUnreadCount,
      chatbotUnreadCount,
      postCommentsCount,
      isSmsModalOpen,
      lastIncomingMessageTimestamp,
    ],
  );

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};

// useContext
const ChatContext = () => {
  return useContext(context);
};

export { ContextProvider, ChatContext };
